import { AlertTriangle, X } from 'lucide-react';
import { Button } from './button.js';
import { useDialogA11y } from '../../lib/use-dialog-a11y.js';

interface ConfirmDialogProps {
  title: string;
  message: string;
  /** Defaults to "Confirm". */
  confirmLabel?: string;
  /** Defaults to "Cancel". */
  cancelLabel?: string;
  /** Tone of the confirm button — `danger` for destructive ops. */
  tone?: 'danger' | 'primary';
  /** Disables the confirm button while a mutation is in flight. */
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Styled replacement for `window.confirm()` — matches the design language
 * of UserDialog / TechDialog and respects dark mode + focus trap.
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const ref = useDialogA11y<HTMLDivElement>(onCancel);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-40 bg-slate-950/40 flex items-center justify-center p-3"
      onClick={onCancel}
    >
      <div
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-xl shadow-xl overflow-hidden"
        style={{ backgroundColor: 'var(--color-elevated-surface)' }}
      >
        <header
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: 'var(--color-border-tertiary)' }}
        >
          <span className="inline-flex items-center gap-2 text-sm font-medium text-ink">
            {tone === 'danger' && (
              <AlertTriangle
                className="h-4 w-4"
                style={{ color: 'var(--feedback-warning-fg)' }}
                aria-hidden
              />
            )}
            {title}
          </span>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-4 text-sm text-ink">{message}</div>
        <footer
          className="flex justify-end gap-2 border-t px-4 py-3"
          style={{ borderColor: 'var(--color-border-tertiary)' }}
        >
          {/* For destructive tones the Cancel button is the initial focus
              target — Enter shouldn't destroy by accident. Primary tones
              autofocus Confirm to make the happy path single-keystroke. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={pending}
            data-autofocus={tone === 'danger' ? true : undefined}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={tone === 'danger' ? 'destructive' : 'primary'}
            disabled={pending}
            onClick={onConfirm}
            data-autofocus={tone === 'danger' ? undefined : true}
          >
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </footer>
      </div>
    </div>
  );
}
